import { handleError, logger } from 'src/logger';
import * as XLSX from 'xlsx';
import { getCurrentUserLocalTimeFormatted } from './DateTimeUtilities';

const EXCEL_EXTENSION = '.xlsx';

interface ColumnWidth {
  wch: number; // width in characters
}

/**
 * Calculates the width of each column based on its content
 * @param json - Data to be exported
 * @returns Object containing column widths
 */
const calculateColumnWidths = (json: any[]): { [key: string]: ColumnWidth } => {
  const columnWidths: { [key: string]: ColumnWidth } = {};

  try {
    // Get all unique keys from the JSON data
    const keys = Array.from(new Set(json.flatMap((obj) => Object.keys(obj))));

    // Initialize with header widths
    keys.forEach((key) => {
      columnWidths[key] = { wch: key.length };
    });

    // Compare with data widths
    json.forEach((row) => {
      keys.forEach((key) => {
        const value = row[key]?.toString() || '';
        const width = value.length;
        if (width > (columnWidths[key]?.wch || 0)) {
          columnWidths[key] = { wch: width };
        }
      });
    });

    // Add some padding and set maximum width
    Object.keys(columnWidths).forEach((key) => {
      // Add 2 characters of padding
      columnWidths[key].wch += 2;
      // Set maximum width to 50 characters
      columnWidths[key].wch = Math.min(columnWidths[key].wch, 50);
    });
  } catch (error) {
    handleError('Error calculating column widths:', error);
  }

  return columnWidths;
};

/**
 * Exports data to an Excel file with auto-adjusted column widths
 * @param json - Data to be exported
 * @param excelFileName - Base name for the Excel file
 * @param worksheetName - Name of the worksheet
 */
export const exportAsExcelFile = (json: any[], excelFileName: string, worksheetName: string): void => {
  try {
    if (!Array.isArray(json) || json.length === 0) {
      throw new Error('Invalid or empty data provided for export');
    }

    const downloadingFileName = `${excelFileName}_${getCurrentUserLocalTimeFormatted()}${EXCEL_EXTENSION}`;

    // Create workbook and worksheet
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);

    // Calculate and set column widths
    worksheet['!cols'] = Object.values(calculateColumnWidths(json));

    // Add some styling (optional)
    // Add header style
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellRef]) continue;

      worksheet[cellRef].s = {
        font: { bold: true },
        alignment: { horizontal: 'center' },
        fill: { fgColor: { rgb: 'CCCCCC' } }
      };
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);

    // Write file
    XLSX.writeFileXLSX(workbook, downloadingFileName);

    logger.info(`Successfully exported Excel file: ${downloadingFileName}`);
  } catch (error) {
    handleError('Error exporting Excel file:', error);
    throw new Error('Failed to export Excel file');
  }
};

//Function to retrieve header from excel file
export const getHeaderRowFromExcel = (file: File): Promise<any[]> => {
  try {
    const _promise: Promise<any[]> = new Promise((resolve) => {
      const headers: string[] = [];
      const reader: FileReader = new FileReader();

      reader.onload = (e: any) => {
        /* read workbook */
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

        /* grab first sheet */
        const workSheetName: string = wb.SheetNames[0];
        const sheet: XLSX.WorkSheet = wb.Sheets[workSheetName];

        if (sheet['!ref']) {
          const range = XLSX.utils.decode_range(sheet['!ref']);
          let C = range.s.r;
          const R = range.s.r;

          /* walk every column in the range */
          for (C = range.s.c; C <= range.e.c; ++C) {
            const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];

            /* find the cell in the first row */
            let hdr = 'UNKNOWN ' + C; // <-- replace with your desired default
            if (cell && cell.t) {
              hdr = XLSX.utils.format_cell(cell);
            }

            headers.push(hdr);
          }
          resolve(headers);
        } else {
          resolve([]);
        }
      };
      reader.readAsBinaryString(file);
    });

    return _promise;
  } catch (error: any) {
    logger.error(`Unable to read the header of the file uploaded. Error: ${error?.message}`, error);
    throw new Error();
  }
};

//Function to read the excel data
export const readExcelFile = (file: File): Promise<any[]> => {
  let jsonData: any[] = [];

  const _promise: Promise<any[]> = new Promise((res) => {
    try {
      const reader: FileReader = new FileReader();
      reader.onload = (e: any) => {
        /* read workbook */
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

        /* grab first sheet */
        const workSheetName: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[workSheetName];

        /* save data */
        jsonData = XLSX.utils.sheet_to_json(ws, { raw: false, header: 0, defval: '' });
        res(jsonData);
      };
      reader.readAsBinaryString(file);
    } catch (error: any) {
      logger.error(`Unable to read the data of the file uploaded. Error: ${error?.message}`, error);
      res([]);
    }
  });
  return _promise;
};
